import React, { useRef, useEffect, useCallback } from "react";
import Editor, { OnMount, OnChange } from "@monaco-editor/react";
import { editor } from "monaco-editor";
import styled from "styled-components";
import { Button, Tooltip } from "antd";
import { FormatPainterOutlined } from "@ant-design/icons";

const EditorContainer = styled.div<{ $hasError?: boolean }>`
  border: 1px solid ${(props) => (props.$hasError ? "#ff4d4f" : "#d9d9d9")};
  border-radius: 6px;
  overflow: hidden;
  transition: border-color 0.3s;

  &:hover {
    border-color: ${(props) => (props.$hasError ? "#ff4d4f" : "#4096ff")};
  }

  &:focus-within {
    border-color: ${(props) => (props.$hasError ? "#ff4d4f" : "#4096ff")};
    box-shadow: 0 0 0 2px
      ${(props) =>
        props.$hasError ? "rgba(255, 77, 79, 0.1)" : "rgba(64, 150, 255, 0.1)"};
  }

  .monaco-editor {
    .monaco-editor-background {
      background-color: #fafafa;
    }
  }

  .monaco-editor.vs-dark {
    .monaco-editor-background {
      background-color: #1e1e1e;
    }
  }
`;

const ErrorMessage = styled.div`
  color: #ff4d4f;
  font-size: 12px;
  margin-top: 4px;
  margin-left: 4px;
`;

const EditorWrapper = styled.div`
  position: relative;
`;

const FormatButton = styled(Button)`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;
  opacity: 0.7;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }
`;

interface JsonEditorProps {
  /** The JSON string value to display in the editor */
  value?: string;
  /** Callback fired when the editor content changes */
  onChange?: (value: string | undefined) => void;
  /** Callback fired when JSON validation occurs */
  onValidate?: (isValid: boolean, errorMessage?: string) => void;
  /** Placeholder text to show when editor is empty */
  placeholder?: string;
  /** Height of the editor (default: 200) */
  height?: number | string;
  /** Whether the editor is read-only (default: false) */
  readOnly?: boolean;
  /** Whether to show validation errors (default: false) */
  showError?: boolean;
  /** External error message to display */
  errorMessage?: string;
  /** Whether to show the format button (default: true) */
  showFormatButton?: boolean;
}

/**
 * A Monaco Editor-based JSON editor component with syntax highlighting,
 * validation, auto-indentation, and formatting features.
 *
 * Features:
 * - JSON syntax highlighting and validation
 * - Auto-indentation and bracket matching
 * - Format JSON button with Ctrl+F shortcut
 * - Real-time validation with error display
 * - Customizable height and read-only mode
 *
 * @example
 * ```tsx
 * <JsonEditor
 *   value={jsonString}
 *   onChange={handleChange}
 *   onValidate={(isValid, error) => setIsValid(isValid)}
 *   height={200}
 *   showError={true}
 * />
 * ```
 */
const JsonEditor: React.FC<JsonEditorProps> = ({
  value = "",
  onChange,
  onValidate,
  placeholder,
  height = 200,
  readOnly = false,
  showError = false,
  errorMessage,
  showFormatButton = true,
}) => {
  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);
  const [internalError, setInternalError] = React.useState<string>();

  const handleEditorDidMount: OnMount = (editor, monaco) => {
    editorRef.current = editor;

    // Configure JSON language options
    monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
      validate: true,
      allowComments: false,
      schemas: [],
      enableSchemaRequest: false,
    });

    // Set editor options for better JSON editing
    editor.updateOptions({
      minimap: { enabled: false },
      scrollBeyondLastLine: false,
      wordWrap: "on",
      wrappingIndent: "indent",
      automaticLayout: true,
      tabSize: 2,
      insertSpaces: true,
      formatOnPaste: true,
      formatOnType: true,
      autoIndent: "full",
      bracketPairColorization: { enabled: true },
      guides: {
        bracketPairs: true,
        indentation: true,
      },
      suggest: {
        showWords: false,
        showKeywords: false,
      },
      quickSuggestions: false,
      parameterHints: { enabled: false },
      hover: { enabled: true },
      folding: true,
      foldingHighlight: true,
      showFoldingControls: "always",
    });

    // Add validation on content change
    editor.onDidChangeModelContent(() => {
      const currentValue = editor.getValue();
      validateJson(currentValue);
    });

    // Add command to format JSON
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyF, () => {
      editor.getAction("editor.action.formatDocument")?.run();
    });

    // Initial validation
    if (value) {
      validateJson(value);
    }
  };

  const validateJson = useCallback(
    (jsonString: string) => {
      if (!jsonString.trim()) {
        setInternalError(undefined);
        onValidate?.(true);
        return;
      }

      try {
        JSON.parse(jsonString);
        setInternalError(undefined);
        onValidate?.(true);
      } catch (error) {
        const errorMsg =
          error instanceof Error ? error.message : "Invalid JSON format";
        setInternalError(errorMsg);
        onValidate?.(false, errorMsg);
      }
    },
    [onValidate],
  );

  const handleEditorChange: OnChange = (value) => {
    onChange?.(value);
  };

  const formatJson = () => {
    if (editorRef.current) {
      const currentValue = editorRef.current.getValue();
      if (currentValue.trim()) {
        try {
          const parsed = JSON.parse(currentValue);
          const formatted = JSON.stringify(parsed, null, 2);
          editorRef.current.setValue(formatted);
          onChange?.(formatted);
        } catch (error) {
          // If parsing fails, just run the format action
          editorRef.current.getAction("editor.action.formatDocument")?.run();
        }
      }
    }
  };

  useEffect(() => {
    if (value && editorRef.current) {
      const currentValue = editorRef.current.getValue();
      if (currentValue !== value) {
        validateJson(value);
      }
    }
  }, [value, validateJson]);

  const displayError = showError && (errorMessage || internalError);
  const hasError = Boolean(displayError);

  return (
    <div>
      <EditorWrapper>
        <EditorContainer $hasError={hasError}>
          <Editor
            height={height}
            language="json"
            value={value}
            onChange={handleEditorChange}
            onMount={handleEditorDidMount}
            options={{
              readOnly,
              contextmenu: true,
              selectOnLineNumbers: true,
              roundedSelection: false,
              scrollbar: {
                vertical: "auto",
                horizontal: "auto",
              },
            }}
            theme="vs"
          />
        </EditorContainer>
        {showFormatButton && !readOnly && (
          <Tooltip title="Format JSON (Ctrl+F)" placement="left">
            <FormatButton
              type="text"
              size="small"
              icon={<FormatPainterOutlined />}
              onClick={formatJson}
            />
          </Tooltip>
        )}
      </EditorWrapper>
      {displayError && (
        <ErrorMessage>{errorMessage || internalError}</ErrorMessage>
      )}
    </div>
  );
};

export default JsonEditor;
